'use client';

import { useState, useRef, useEffect } from 'react';

export interface FilterOption {
  value: string;
  label: string;
  icon: string;
  requiresValue?: boolean;
  requiresTwoValues?: boolean;
}

export interface AdvancedFilterProps {
  value: any;
  onChange: (value: any) => void;
  placeholder?: string;
  columnType?: string;
  width?: number;
  originalWidth?: number;
}

const filterOptions: FilterOption[] = [
  { value: 'contains', label: 'Contains', icon: 'ri-search-line', requiresValue: true },
  { value: 'not_contains', label: 'Does not contain', icon: 'ri-forbid-line', requiresValue: true },
  { value: 'starts_with', label: 'Starts with', icon: 'ri-text-direction-l', requiresValue: true },
  { value: 'ends_with', label: 'Ends with', icon: 'ri-text-direction-r', requiresValue: true },
  { value: 'equals', label: 'Equals', icon: 'ri-equal-line', requiresValue: true },
  { value: 'not_equals', label: 'Does not equal', icon: 'ri-close-line', requiresValue: true },
  { value: 'between', label: 'Between', icon: 'ri-expand-left-right-line', requiresTwoValues: true },
  { value: 'reset', label: 'Reset', icon: 'ri-refresh-line', requiresValue: false },
];

export const AdvancedFilter: React.FC<AdvancedFilterProps> = ({
  value,
  onChange,
  placeholder = "Filter...",
  columnType = "text",
  width,
  originalWidth
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<FilterOption>(filterOptions[0]);
  const [filterValue, setFilterValue] = useState('');
  const [filterValue2, setFilterValue2] = useState('');
  const [currentWidth, setCurrentWidth] = useState(width || originalWidth || 200);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Parse existing filter value
  useEffect(() => {
    if (value && typeof value === 'object' && value.type) {
      const option = filterOptions.find(opt => opt.value === value.type);
      if (option) {
        setSelectedFilter(option);
        setFilterValue(value.value || '');
        setFilterValue2(value.value2 || '');
      }
    } else if (typeof value === 'string') {
      // Default to contains for simple string values
      setSelectedFilter(filterOptions[0]);
      setFilterValue(value);
    }
  }, [value]);

  // Force re-render when width changes
  useEffect(() => {
    const newWidth = width || originalWidth || 200;
    if (newWidth !== currentWidth) {
      setCurrentWidth(newWidth);
    }
  }, [width, originalWidth, currentWidth]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFilterSelect = (option: FilterOption) => {
    setSelectedFilter(option);
    setIsOpen(false);
    
    if (option.value === 'reset') {
      setFilterValue('');
      setFilterValue2('');
      onChange(undefined);
      return;
    }

    // Apply filter immediately if no value is required
    if (!option.requiresValue && !option.requiresTwoValues) {
      applyFilter(option, filterValue, filterValue2);
    } else {
      // Focus input after selection
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const applyFilter = (option: FilterOption, val1: string, val2?: string) => {
    if (option.value === 'reset') {
      onChange(undefined);
      return;
    }

    if (option.requiresTwoValues && (!val1 || !val2)) {
      return; // Don't apply incomplete between filters
    }

    if (option.requiresValue && !val1) {
      return; // Don't apply empty filters
    }

    // For simple contains filter, just pass the string value for backward compatibility
    if (option.value === 'contains' && val1) {
      onChange(val1);
      return;
    }

    // For advanced filters, pass an object
    const filterObj = {
      type: option.value,
      value: val1,
      ...(option.requiresTwoValues && { value2: val2 })
    };

    onChange(filterObj);
  };

  const handleInputChange = (val: string, isSecondValue = false) => {
    if (isSecondValue) {
      setFilterValue2(val);
      applyFilter(selectedFilter, filterValue, val);
    } else {
      setFilterValue(val);
      applyFilter(selectedFilter, val, filterValue2);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      applyFilter(selectedFilter, filterValue, filterValue2);
    }
  };

  return (
    <div
      className="relative w-full"
      ref={dropdownRef}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent bg-white dark:bg-gray-700">
        {/* Filter Type Dropdown Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          className="flex items-center px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 border-r border-gray-300 dark:border-gray-600"
        >
          <i className={`${selectedFilter.icon} w-3 h-3 mr-1`}></i>
          <i className="ri-arrow-down-s-line w-3 h-3"></i>
        </button>

        {/* Input Field(s) */}
        <div className="flex items-center flex-1 overflow-hidden min-w-0">
          {selectedFilter.requiresValue || selectedFilter.requiresTwoValues ? (
            <>
              <input
                ref={inputRef}
                type={columnType === 'number' ? 'number' : 'text'}
                value={filterValue}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={handleInputKeyDown}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                placeholder={selectedFilter.requiresTwoValues ? "From" : "Filter..."}
                className="flex-1 min-w-0 px-1 py-1 text-xs border-0 focus:ring-0 focus:outline-none overflow-hidden text-ellipsis bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
              />
              {selectedFilter.requiresTwoValues && (
                <>
                  <span className="px-1 py-1 text-xs text-gray-400 dark:text-gray-500">-</span>
                  <input
                    type={columnType === 'number' ? 'number' : 'text'}
                    value={filterValue2}
                    onChange={(e) => handleInputChange(e.target.value, true)}
                    onKeyDown={handleInputKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    placeholder="To"
                    className="flex-1 min-w-0 px-1 py-1 text-xs border-0 focus:ring-0 focus:outline-none overflow-hidden text-ellipsis bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </>
              )}
            </>
          ) : (
            <div className="flex-1 px-2 py-1 text-xs text-gray-500 dark:text-gray-400 truncate">
              {selectedFilter.label}
            </div>
          )}
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
          {filterOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleFilterSelect(option)}
              className="w-full flex items-center px-3 py-2 text-xs text-left hover:bg-gray-50 dark:hover:bg-gray-700 focus:bg-gray-50 dark:focus:bg-gray-700 focus:outline-none"
            >
              <i className={`${option.icon} w-4 h-4 mr-2 text-gray-500 dark:text-gray-400`}></i>
              <span className="text-gray-700 dark:text-gray-200">{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
